import { z } from 'zod';

/**
 * Слой адаптеров интеграций (раздел 7 ТЗ, ИН-01–04, ИН-05). Полей обмена с 1С пока нет: портал не выдумывает формат,
 * а хранит настраиваемое сопоставление «поле портала → поле внешней системы» и очередь обмена.
 */

/** Виды адаптеров. «stub» — заглушка для проверки очереди, остальные — заготовки под согласованный формат */
export const CONNECTOR_KINDS = {
  stub: 'Заглушка (проверка очереди)',
  onec_file: '1С — обмен файлами',
  onec_http: '1С — HTTP-сервисы или OData (2 очередь)',
  ncalayer: 'ЭЦП через NCALayer',
  gdrive_read: 'Google Диск — только чтение',
  telematics: 'Телематика (Wialon, Omnicomm, Teltonika)',
  money_portal: 'Money-портал — факт оплат (банк и 1С)',
} as const;
export type ConnectorKind = keyof typeof CONNECTOR_KINDS;
export const CONNECTOR_KIND_CODES = Object.keys(CONNECTOR_KINDS) as [ConnectorKind, ...ConnectorKind[]];

/** Режим: «только файл» — очередь не уходит сама, администратор скачивает файл и вносит номера 1С вручную */
export const CONNECTOR_MODES = { file: 'Только файл', live: 'Автоматически' } as const;
export type ConnectorMode = keyof typeof CONNECTOR_MODES;

/** Сущности портала, которые уходят во внешние системы. Точки подключения потоков — по этим кодам */
export const EXCHANGE_ENTITIES = {
  payment_request: 'Заявка на оплату',
  payment_registry: 'Реестр платежей',
  warehouse_document: 'Документ склада',
  contract: 'Договор',
  counterparty: 'Контрагент',
  payment_fact: 'Факт оплаты',
  kpi_payroll: 'Показатели для премий (КП)',
  telematics_reading: 'Показание телематики',
  signature: 'Подпись документа (ЭЦП)',
} as const;
export type ExchangeEntity = keyof typeof EXCHANGE_ENTITIES;
export const EXCHANGE_ENTITY_CODES = Object.keys(EXCHANGE_ENTITIES) as [ExchangeEntity, ...ExchangeEntity[]];

export const QUEUE_STATUSES = {
  pending: 'Ждёт отправки',
  sending: 'Отправляется',
  file_ready: 'В файле, ждёт номер 1С',
  sent: 'Отправлено',
  confirmed: 'Подтверждено внешней системой',
  failed: 'Ошибка, будет повтор',
  dead: 'Остановлено, нужна проверка',
  cancelled: 'Отменено',
} as const;
export type QueueStatus = keyof typeof QUEUE_STATUSES;
export const QUEUE_STATUS_CODES = Object.keys(QUEUE_STATUSES) as [QueueStatus, ...QueueStatus[]];

/** Повтор через 5, 15 и 60 минут, дальше раз в час (ИН-02); после MAX_AUTO_ATTEMPTS — только вручную */
export const RETRY_MINUTES = [5, 15, 60] as const;
export const HOURLY_RETRY_MINUTES = 60;
export const MAX_AUTO_ATTEMPTS = 24;
export const retryDelayMinutes = (attemptsDone: number): number =>
  RETRY_MINUTES[attemptsDone - 1] ?? HOURLY_RETRY_MINUTES;

const Code = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[A-Za-z0-9_.-]+$/);

export const ConnectorView = z.object({
  id: z.uuid(),
  kind: z.enum(CONNECTOR_KIND_CODES),
  kindTitle: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  mode: z.enum(['file', 'live']),
  /** Несекретные настройки: адрес, расписание */
  config: z.record(z.string(), z.unknown()),
  /** Секрет внесён; сам ключ никогда не возвращается */
  hasSecret: z.boolean(),
  /** Какие режимы адаптер умеет */
  modes: z.array(z.enum(['file', 'live'])),
  entities: z.array(z.enum(EXCHANGE_ENTITY_CODES)),
  /** Адаптер готов к работе или это заготовка под согласованный формат */
  ready: z.boolean(),
  externalChannelsOff: z.boolean(),
  counts: z.object({ pending: z.number(), failed: z.number(), dead: z.number(), fileReady: z.number() }),
  lastSuccessAt: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type ConnectorView = z.infer<typeof ConnectorView>;

export const ConnectorCreate = z.strictObject({
  kind: z.enum(CONNECTOR_KIND_CODES),
  name: z.string().trim().min(2).max(120),
});

export const ConnectorPatch = z.strictObject({
  name: z.string().trim().min(2).max(120).optional(),
  enabled: z.boolean().optional(),
  mode: z.enum(['file', 'live']).optional(),
  config: z.record(z.string().max(60), z.union([z.string().max(500), z.number(), z.boolean()])).optional(),
  /** Запись ключа: только на запись, прочитать нельзя; null — стереть */
  secret: z.string().min(1).max(4000).nullable().optional(),
});

export const FieldMapRow = z.strictObject({
  portalField: Code,
  externalField: z.string().trim().min(1).max(120),
  /** Правило: как есть, дата ДД.ММ.ГГГГ, сумма с запятой, константа */
  transform: z.enum(['as_is', 'date_dmy', 'decimal_comma', 'constant']).default('as_is'),
  constantValue: z.string().max(200).nullable().default(null),
  required: z.boolean().default(false),
});
export type FieldMapRow = z.infer<typeof FieldMapRow>;

export const FieldMapPut = z.strictObject({
  rows: z.array(FieldMapRow).max(200),
});

export const ExchangeEnqueue = z.strictObject({
  entity: z.enum(EXCHANGE_ENTITY_CODES),
  entityId: z.uuid(),
  /** Ключ, по которому повтор не создаёт дубль: тот же ключ — та же запись очереди */
  idempotencyKey: z.string().trim().min(1).max(200),
  payload: z.record(z.string(), z.unknown()),
});

export const QueueItemView = z.object({
  id: z.uuid(),
  connectorId: z.uuid(),
  connectorName: z.string(),
  entity: z.enum(EXCHANGE_ENTITY_CODES),
  entityTitle: z.string(),
  entityId: z.uuid().nullable(),
  status: z.enum(QUEUE_STATUS_CODES),
  statusTitle: z.string(),
  attempts: z.number(),
  nextAttemptAt: z.string().nullable(),
  lastError: z.string().nullable(),
  externalRef: z.string().nullable(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
  /** Обязательные поля сопоставления, которых нет в записи */
  missingFields: z.array(z.string()),
});
export type QueueItemView = z.infer<typeof QueueItemView>;

export const QueueQuery = z.object({
  connectorId: z.uuid().optional(),
  status: z.enum(QUEUE_STATUS_CODES).optional(),
  entity: z.enum(EXCHANGE_ENTITY_CODES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const LogEntryView = z.object({
  id: z.uuid(),
  at: z.string(),
  direction: z.enum(['out', 'in']),
  ok: z.boolean(),
  message: z.string(),
  attempt: z.number(),
});

export const QueueConfirm = z.strictObject({
  externalRef: z.string().trim().min(1).max(200),
});

export const UnmappedView = z.object({
  id: z.uuid(),
  connectorId: z.uuid(),
  kind: z.enum(['counterparty', 'item', 'warehouse', 'contract', 'cost_article']),
  key: z.string(),
  label: z.string(),
  status: z.enum(['open', 'mapped', 'ignored']),
  mappedTo: z.string().nullable(),
});
export const UnmappedResolve = z.strictObject({
  status: z.enum(['mapped', 'ignored']),
  mappedTo: z.string().trim().min(1).max(200).nullable().default(null),
});

/** Заготовка ИН-08: что браузер отправляет в NCALayer (websocket на компьютере пользователя) */
export const SignRequestView = z.object({
  module: z.literal('kz.gov.pki.knca.commonUtils'),
  method: z.string(),
  args: z.array(z.unknown()),
});
export type SignRequestView = z.infer<typeof SignRequestView>;
